import FirstLineContainer from "components/common/FirstLineContainer";
import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Filter from "./Filter";
import { Box, Button, Stack, Typography } from "@mui/material";
import StyledDataGrid from "styles/styledDataGrid";
import CreateRelease from "./CreateRelease";
import api from "api";
import dayjs from "dayjs";
import { UserContext } from "context";
import { useDispatch } from "react-redux";
import { setDialog } from "slices/appSlice";
import { setHealthPlan } from "slices/hpSlice";
import { ALL, DONE, IN_FLIGHT, NOT_READY, READY } from "constants/Constants";
import CustomGridToolbar from "components/GridToolbar";

function ReleasePlan() {
  const [loading, setLoading] = useState(false);
  const [releaseData, setReleaseData] = useState([]);
  const [createRelease, setCreateRelease] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [editable, setEditable] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState(ALL);
  const [selectedStatus, setSelectedStatus] = useState(ALL);

  const userContext = useContext(UserContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isAdmin = useMemo(() => {
    if (!userContext) return false;
    return (
      userContext.groups[0] === "POET-Avalon-Admin" ||
      (userContext.POETSkill &&
        userContext.POETSkill.some((skill) => skill.toLowerCase() === "admin"))
    );
  }, [userContext]);

  useEffect(() => {
    if (userContext) {
      fetchReleaseData();
    }
  }, [userContext]);

  const reloadParent = () => {
    fetchReleaseData();
  };

  useEffect(() => {
    if (selectedRow && selectedRow.releaseScheduleKey) {
      navigate(
        `/release-plan/details/${selectedRow.releaseScheduleKey}/${selectedRow.releaseNumber}`,
        {
          state: { editable, from: location.pathname },
        }
      );
      setSelectedRow(null);
    }
  }, [selectedRow, editable, navigate, location.pathname]);

  const handleReleaseEdit = (row, editableFlag) => {
    if (row.healthPlanDisplayName) {
      const hp = {
        healthPlanKey: row.healthPlanKey,
        healthPlanName: row.healthPlanName,
        healthPlanDisplayName: row.healthPlanDisplayName,
      };
      dispatch(setHealthPlan(hp));
    }
    setSelectedRow(row);
    setEditable(editableFlag);
  };

  // Figure out row-level status for filtering
  const getStatus = (row) => {
    const {
      codingPercentComplete,
      translationPercentComplete,
      configurationPercentComplete,
    } = row;
    if (
      codingPercentComplete === 1 &&
      translationPercentComplete === 1 &&
      configurationPercentComplete === 1
    ) {
      return DONE;
    }
    if (
      codingPercentComplete === 0 &&
      translationPercentComplete === 0 &&
      configurationPercentComplete === 0
    ) {
      return READY;
    }
    return IN_FLIGHT;
  };

  // Apply pipeline + status filters
  const filteredData = useMemo(() => {
    return releaseData.filter((row) => {
      const matchesPipeline =
        selectedPipeline === ALL ||
        row.pipelineStatusText?.toLowerCase() ===
          selectedPipeline.toLowerCase();

      const matchesStatus =
        selectedStatus === ALL || getStatus(row) === selectedStatus;

      return matchesPipeline && matchesStatus;
    });
  }, [releaseData, selectedPipeline, selectedStatus]);

  const columns = [
    { field: "releaseScheduleKey", headerName: "Release Key", flex: 0.5 },
    { field: "healthPlanName", headerName: "Health Plan", flex: 1 },
    {
      field: "healthPlanDisplayName",
      headerName: "Health Plan Display Name",
      flex: 2,
    },
    { field: "releaseNumber", headerName: "Release #", flex: 1 },
    {
      field: "targetUatDate",
      headerName: "Target UAT",
      flex: 1,
      valueFormatter: (params) => {
        return params ? dayjs(params).format("MM/DD/YYYY") : "";
      },
    },
    {
      field: "targetProdDate",
      headerName: "Target Prod",
      flex: 1,
      valueFormatter: (params) => {
        return params ? dayjs(params).format("MM/DD/YYYY") : "";
      },
    },
    { field: "policyImpact", headerName: "Policy Impact", flex: 1 },
    {
      field: "codingPercentComplete",
      headerName: "Coding",
      flex: 1,
      valueGetter: (params) =>
        params != null ? `${Math.round(params * 100)}%` : "N/A",
    },
    {
      field: "translationPercentComplete",
      headerName: "Translation",
      flex: 1,
      valueGetter: (params) =>
        params != null ? `${Math.round(params * 100)}%` : "N/A",
    },
    {
      field: "configurationPercentComplete",
      headerName: "Configuration",
      flex: 1,
      valueGetter: (params) =>
        params != null ? `${Math.round(params * 100)}%` : "N/A",
    },
    {
      field: "pipelineStatusText",
      headerName: "Pipeline Status",
      flex: 1.5,
    },
    {
      field: "actions",
      headerName: "",
      flex: 0.5,
      renderCell: (params) => (
        <Button
          variant="text"
          color="info"
          onClick={() => handleReleaseEdit(params.row, isAdmin)}
        >
          {isAdmin ? "Edit" : "View"}
        </Button>
      ),
    },
  ];

  const fetchReleaseData = async () => {
    setLoading(true);
    try {
      const response = await api.ReleaseSchedule.getAllReleaseSchedules();
      setReleaseData(response.data);
    } catch (error) {
      dispatch(
        setDialog({
          message:
            error.response?.data?.errors ||
            "Failed to retrieve the List of Release Plans",
          title: "Error",
        })
      );
      console.error("Error fetching release schedules:", error);
    } finally {
      setLoading(false);
    }
  };

  // Build the left side actions that will be injected into CustomGridToolbar
  const exportComponent = isAdmin ? (
    <Button
      variant="contained"
      onClick={() => setCreateRelease(true)}
      size="small"
    >
      Create Release
    </Button>
  ) : null;

  if (!userContext) return null;

  return (
    <>
      <CreateRelease
        open={createRelease}
        handleClose={() => setCreateRelease(false)}
        reloadParent={reloadParent}
      />

      <Box sx={{ height: "90vh", display: "flex", flexDirection: "column" }}>
        <Stack spacing={1} marginBottom={1}>
          <FirstLineContainer>
            <Typography variant="h6" sx={{ textAlign: "left", flex: 1 }}>
              <Box component="span" fontWeight="fontWeightBold">
                Release Plan
              </Box>
            </Typography>
          </FirstLineContainer>

          <Filter
            selectedPipeline={selectedPipeline}
            setSelectedPipeline={setSelectedPipeline}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
          />
        </Stack>

        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
          }}
        >
          <StyledDataGrid
            columns={columns}
            rows={filteredData}
            loading={loading}
            getRowId={(row) => row.releaseScheduleKey}
            disableExtendRowFullWidth
            onRowClick={(params) => handleReleaseEdit(params.row, isAdmin)}
            initialState={{
              sorting: {
                sortModel: [{ field: "healthPlanName", sort: "asc" }],
              },
            }}
            slots={{
              toolbar: CustomGridToolbar,
            }}
            slotProps={{
              toolbar: {
                exportComponent,
              },
            }}
            sx={{
              "& .MuiDataGrid-row": {
                cursor: "pointer",
              },
            }}
          />
        </Box>
      </Box>
    </>
  );
}

export default ReleasePlan;
